import { clock, dayMonthMaybeYear } from "@/lib/dates";
import type { Job, JobRun, ScheduleMode, Trigger } from "@/lib/jobs";

import { tallyLabel } from "./jobText";

/**
 * The strings the Jobs panel writes. Pure, so they are tested directly — every one of them is a
 * claim about when something ran or will run, and a wrong one is worse than a blank.
 */

// One row of characters for both, now that the initials are Japanese: a day toggle showing 日 and
// a pill reading 日曜 would be two spellings of one weekday two inches apart. `DAY_INITIALS` stays
// a separate export because the toggles are a fixed-width strip and the pills are prose.
export const DAY_INITIALS = ["日", "月", "火", "水", "木", "金", "土"] as const;
const DAY_NAMES = DAY_INITIALS;

export const MODES: { mode: ScheduleMode; label: string }[] = [
  { mode: "manual", label: "手動のみ" },
  { mode: "interval", label: "N日ごと" },
  { mode: "daysOfWeek", label: "曜日" },
  { mode: "dayOfMonth", label: "毎月N日" },
];

/** The mobile schedule card's sub-line: what the mode actually means, spelled out. */
export function modeDescription(mode: ScheduleMode, trigger: Trigger): string {
  switch (mode) {
    case "manual":
      return "実行したときだけ";
    case "interval":
      return `前回の実行から${trigger.everyNDays ?? 7}日ごと`;
    case "daysOfWeek": {
      const days = trigger.days.length ? trigger.days : [0];
      return `${days.map((d) => DAY_NAMES[d]).join("・")} ${trigger.time}`;
    }
    case "dayOfMonth":
      return `毎月${trigger.dayOfMonth ?? 1}日 ${trigger.time}`;
  }
}

/**
 * A place in line — `1番目`.
 *
 * **The English suffix table is gone rather than translated**, and that is the point: `1st`/`2nd`
 * needed four irregular cases and a `11..13` exception, and Japanese ordinals are the number with
 * 番目 after it, always. The function survives so the call sites do not have to know that.
 */
export function ordinal(n: number): string {
  return `${n}番目`;
}

/** The trigger cell: `Manual`, or a schedule pill like `Weekly · Sun 03:00`. */
export function triggerLabel(trigger: Trigger): string {
  switch (trigger.mode) {
    case "manual":
      return "手動";
    case "interval": {
      const n = trigger.everyNDays ?? 1;
      return `${n === 1 ? "毎日" : `${n}日ごと`} · ${trigger.time}`;
    }
    case "daysOfWeek": {
      const days = trigger.days;
      if (days.length === 7) return `毎日 · ${trigger.time}`;
      const named = days.map((d) => DAY_NAMES[d]).join("");
      return `毎週 · ${named} ${trigger.time}`;
    }
    case "dayOfMonth":
      return `毎月 · ${trigger.dayOfMonth ?? 1}日 ${trigger.time}`;
  }
}

/**
 * The `Next` column: `in 4 days`, or an absolute date once that stops being useful.
 *
 * Relative up to a week because "in 4 days" is what tells you whether to bother running it by
 * hand; past that the date is the more honest answer.
 */
export function nextRunLabel(at: number | null, now: Date = new Date()): string {
  if (at === null) return "—";
  const ms = at * 1000 - now.getTime();
  if (ms <= 0) return "期限";
  const minutes = Math.round(ms / 60000);
  if (minutes < 60) return `${minutes}分後`;
  const hours = Math.round(minutes / 60);
  if (hours < 24) return `${hours}時間後`;
  const days = Math.round(hours / 24);
  if (days <= 7) return `${days}日後`;
  return timestamp(at, now);
}

/**
 * `1 Aug 04:00`, or `1 Aug 2025 04:00` for a year that is not this one.
 *
 * The format itself lives in `lib/dates.ts`, shared with the Cards Due column — this used to be the
 * only place that spelled a date out, under a comment describing what the other formatter did as
 * the thing to avoid.
 *
 * Every export here takes `now` as a `Date`. It is a parameter at all so the tests can pin a moment
 * — left to the wall clock they would quietly change meaning at the year boundary, gaining a year
 * while the assertions kept passing.
 */
export function timestamp(epochSeconds: number, now: Date = new Date()): string {
  const d = new Date(epochSeconds * 1000);
  return `${dayMonthMaybeYear(d, now)} ${clock(d)}`;
}

/**
 * The one place a verdict becomes a word.
 *
 * It was private, and two call sites therefore grew their own — `JobDetail` and `RunHistory` both
 * did `verdict.charAt(0).toUpperCase() + verdict.slice(1)`, which rendered `Passed` / `Failed` /
 * `Cancelled` in English beside rows this map had already translated. Exported so there is nothing
 * to reimplement; unknown verdicts fall through to themselves rather than to a blank.
 */
const VERDICT_WORD: Record<string, string> = {
  passed: "成功",
  failed: "失敗",
  cancelled: "中止",
  running: "実行中",
  queued: "待機",
  idle: "未実行",
};

export function verdictWord(verdict: string): string {
  return VERDICT_WORD[verdict] ?? verdict;
}

/**
 * The `Next` column — 日本-307.
 *
 * `—` for a running or queued row, because **the next run is the one in front of you** and a future
 * time there is noise; it comes back on its own once the row settles. `Disabled` for a row whose
 * time and priority collide with another job's, which is a different statement entirely: that one
 * has a schedule and it will not be honoured.
 *
 * The server keeps `nextRunAt` honest through all of this rather than nulling it, so this is the one
 * place that decides what a reader is shown.
 */
export function nextColumn(job: Job, now: Date = new Date()): string {
  if (job.blocked) return "無効";
  if (job.state === "running" || job.state === "queued") return "—";
  return nextRunLabel(job.nextRunAt, now);
}

/** `Queued · 1st in line`. */
export function queuedLabel(position: number | null): string {
  return position === null ? "待機" : `待機 · ${ordinal(position)}`;
}

/**
 * The mono line under a queued row: what it is waiting for, and what the row was showing before.
 *
 * **No estimated start time**, deliberately — the design frame draws `est. start 03:02` and Alex's
 * call was to drop it. It could only be extrapolated from previous durations, which do not exist for
 * a job that has never run and swing wildly for the one that fetches audio. A start time wrong by
 * ten minutes is worse than a place in line that is exactly right.
 */
export function queuedDetail(job: Job, now: Date = new Date()): string {
  const parts: string[] = [];
  if (job.trigger.mode !== "manual") parts.push(triggerLabel(job.trigger));
  if (!job.lastRun) {
    parts.push("未実行");
  } else {
    // No `toLowerCase()` any more — it did nothing to a Japanese verdict and would silently case
    // a future English one, which is the kind of half-applied rule that reads as a bug.
    const verdict = verdictWord(job.lastRun.verdict);
    parts.push(`前回${verdict} ${timestamp(job.lastRun.startedAt, now)}`);
  }
  return parts.join(" · ");
}

/**
 * How many OTHER scheduled jobs share this job's time of day and zone.
 *
 * Feeds the editor's *"Two other jobs also run at 03:00."* — the hint that makes the priority field
 * mean something before you have collided with anything.
 */
export function jobsSharingTime(jobs: Job[], job: Job): number {
  if (job.trigger.mode === "manual") return 0;
  return jobs.filter(
    (other) =>
      other.id !== job.id &&
      other.trigger.mode !== "manual" &&
      other.trigger.time === job.trigger.time &&
      other.trigger.timezone === job.trigger.timezone,
  ).length;
}

/** The verdict line: `Passed · 27 Jul 03:00`, or `Never run`. */
export function lastRunLabel(run: JobRun | null, now: Date = new Date()): string {
  if (!run) return "未実行";
  const word = VERDICT_WORD[run.verdict] ?? run.verdict;
  return `${word} · ${timestamp(run.startedAt, now)}${run.wasLate ? "（遅延）" : ""}`;
}

export function duration(ms: number): string {
  // Round to whole seconds FIRST, then split. Splitting first and rounding the remainder
  // independently renders `1m 60s` at 119.5s and `60s` at 59.9s — the two halves disagreeing
  // about which second it is.
  const seconds = Math.round(ms / 1000);
  if (seconds < 60) return `${seconds}秒`;
  const m = Math.floor(seconds / 60);
  return `${m}分${String(seconds - m * 60).padStart(2, "0")}秒`;
}

export interface TallyEntry {
  label: string;
  value: number;
  /** A unit the key declared, rendered after the value. Empty for a plain count. */
  suffix: string;
}

/** A trailing ` (X)` on a tally key is its unit — `Snapshot size (MB)` → `12.4 MB`. */
const UNIT = /\s*\(([^()]+)\)\s*$/;

/**
 * The run's numbers, ready to render — 日本-229.
 *
 * `JobOutcome.tally` was persisted, served and typed for months and read by nothing, so every
 * surface showed `run.detail` instead: a `" · ".join(parts)` each job assembled for itself, which
 * duplicated the tally beside it and drifted from it. This is the single reader, and both the tile
 * card and the one-line summary below come from it, so no job builds a display string again.
 *
 * **Zeros are kept.** `No source found 0` is the reassurance you actually want from the audio job,
 * and dropping empty keys would make the card change shape depending on the data.
 *
 * **Insertion order is the server's**, and it is meaningful: JOBS.md wants what succeeded before
 * what refused, and every job already lists its keys that way. Sorting here would throw that away.
 */
export function tallyEntries(run: JobRun | null): TallyEntry[] {
  if (!run) return [];
  return Object.entries(run.tally).map(([key, value]) => {
    const unit = UNIT.exec(key);
    // The English key is the stable handle and is what `jobText` is keyed on; the unit comes off
    // it first, so the table there holds `Saved` rather than `Saved (MB)`.
    const stored = unit ? key.slice(0, unit.index) : key;
    return { label: tallyLabel(stored), value, suffix: unit?.[1] ?? "" };
  });
}

/**
 * The mono line under a verdict, in every row too narrow for the tiles.
 *
 * Composed here rather than on the server: `detail` now carries only what a number cannot say —
 * which fields were added, which file was written, why a run stopped — so the numbers have to be
 * rendered from the tally, and the duration is a field of its own rather than something the runner
 * pastes onto a string.
 */
export function runSummary(run: JobRun | null): string {
  if (!run) return "";
  const numbers = tallyEntries(run).map(
    ({ label, value, suffix }) =>
      // No `.toLowerCase()`: Japanese has no case, so it was a no-op the moment the labels moved —
      // and a silent one, which is worse than a removed call.
      `${value.toLocaleString()}${suffix ? ` ${suffix}` : ""} ${label}`,
  );
  const parts = [...numbers, run.detail].filter(Boolean);
  return parts.length > 0 ? parts.join(" · ") : duration(run.durationMs);
}

/**
 * The running cell: `Running · 412 / 1,204`.
 *
 * **Count before percentage** — 412 of 1,204 is what tells you whether to wait; 34% does not.
 */
export function runningLabel(current: number, total: number): string {
  return total > 0
    ? `実行中 · ${current.toLocaleString()} / ${total.toLocaleString()}`
    : "実行中";
}

/** `38s elapsed · ~1m 20s left`, once there is enough of a rate to extrapolate from. */
export function elapsedLabel(elapsedSeconds: number, current: number, total: number): string {
  const elapsed = duration(elapsedSeconds * 1000);
  if (current < 2 || total <= current) return `経過${elapsed}`;
  const remaining = ((total - current) * elapsedSeconds) / current;
  return `経過${elapsed} · 残り約${duration(remaining * 1000)}`;
}

export function percent(current: number, total: number): number {
  if (total <= 0) return 0;
  return Math.min(100, Math.round((current / total) * 100));
}

/**
 * `5 jobs · 2 scheduled · 1 running` — the panel header's summary.
 *
 * The blocked count is spelled out in full rather than shortened to match its neighbours. It is the
 * one entry here naming a **misconfiguration** rather than a state, and `2 blocked` alone would send
 * you looking for whatever is blocking them.
 */
export function jobsSummary(jobs: Job[]): string {
  const parts = [`ジョブ${jobs.length}`];
  const scheduled = jobs.filter((j) => j.trigger.mode !== "manual").length;
  if (scheduled) parts.push(`予定${scheduled}`);
  const running = jobs.filter((j) => j.state === "running").length;
  if (running) parts.push(`実行中${running}`);
  const queued = jobs.filter((j) => j.state === "queued").length;
  if (queued) parts.push(`待機${queued}`);
  const failed = jobs.filter((j) => j.state === "failed").length;
  if (failed) parts.push(`失敗${failed}`);
  const blocked = jobs.filter((j) => j.blocked).length;
  if (blocked) parts.push(`優先度の衝突で無効${blocked}`);
  return parts.join(" · ");
}

/**
 * The mobile row's status line — everything the desktop spreads across three columns.
 *
 * `Sun 03:00 · passed 27 Jul`, `Running · 412 / 1,204`, `Failed · 17 errors`, `Never run`.
 */
export function mobileStatus(
  job: Job,
  live: { current: number; total: number } | null,
  now: Date = new Date(),
): string {
  if (job.state === "running") {
    return live ? runningLabel(live.current, live.total) : "実行中";
  }
  if (job.state === "queued") return queuedLabel(job.queuePosition);
  if (job.blocked) return `${triggerLabel(job.trigger)} · 無効`;
  if (!job.lastRun) {
    return job.trigger.mode === "manual" ? "未実行" : `${triggerLabel(job.trigger)} · 未実行`;
  }
  if (job.state === "failed") {
    const n = job.lastRun.errorCount;
    return n
      ? `失敗 · エラー${n}`
      : `失敗 · ${timestamp(job.lastRun.startedAt, now)}`;
  }
  const when = timestamp(job.lastRun.startedAt, now);
  const verdict = job.state === "cancelled" ? "中止" : "成功";
  return job.trigger.mode === "manual"
    ? `${verdict} · ${when}`
    : `${triggerLabel(job.trigger)} · ${verdict} ${when}`;
}

/**
 * The verb in the row's action button.
 *
 * A failed job reads `Run now` here and `Retry N failed` in its expanded body — never two
 * competing primaries in one row.
 *
 * **A queued row reads `Cancel` too**, and it means something different from the running one: it
 * drops the pending run without touching the schedule, where cancelling a running job stops work
 * already under way. One verb for both, because the row only ever offers one action and "get me out
 * of this run" is the same intent at either stage.
 */
export function actionVerb(job: Job): "今すぐ実行" | "中止" {
  return job.state === "running" || job.state === "queued" ? "中止" : "今すぐ実行";
}
